import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getAdminConfig, setAdminConfig } from 'services'

interface IHmdaActionTakenResponse {
  config: Record<string, string>
  actionTakenOptions: Record<string, string>
  loanStatusLabels: Record<string, string>
}

export const HmdaActionTakenConfiguration = () => {
  const [isLoading, setLoading] = useState(true)
  const [config, setConfig] = useState<Record<string, string>>({})
  const [statusLabels, setStatusLabels] = useState<Record<string, string>>({})
  const [actionTakenOptions, setActionTakenOptions] = useState<Record<string, string>>({})

  const [loanGlobalStatus] = useSelector((state: any) => [state.loanGlobalStatus])

  useEffect(() => {
    getAdminConfig('hmdaActionTaken')
      .then((res: IHmdaActionTakenResponse) => {
        setConfig(res?.config || {})
        setStatusLabels(res?.loanStatusLabels || {})
        setActionTakenOptions(res?.actionTakenOptions || {})
      })
      .finally(() => setLoading(false))
  }, [])

  const onChange = async (statusKey: string, value: string) => {
    const next = cloneDeep(config)
    if (!value) delete next[statusKey]
    else next[statusKey] = value
    setConfig(next)
    setLoading(true)
    try {
      await setAdminConfig('hmdaActionTaken', next)
    } finally {
      setLoading(false)
    }
  }

  // Prefer the configured loan status labels (from LoanStatusConfig) and
  // fall back to the redux-cached global status map. This keeps ordering and
  // naming consistent with the rest of the app even if the user has renamed
  // a status.
  const mergedStatuses: Record<string, string> = { ...loanGlobalStatus, ...statusLabels }
  const statusKeys = Object.keys(mergedStatuses)

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <div className="border-b-2 border-shade-blue w-full italic flex justify-between items-center mb-4">
        <p>- Action Taken per Loan Status</p>
      </div>
      <div className="mt-2">
        <table className="w-full text-sm">
          <thead className="uppercase text-xs bg-gray-50 dark:bg-gray-700">
            <tr>
              {['No', 'Status', 'Action Taken Title'].map((v) => (
                <th scope="col" className="p-3 border" key={`header-${v}`}>
                  {v}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {statusKeys.map((statusKey, index) => {
              const current = config[statusKey] || ''
              return (
                <tr key={statusKey}>
                  <td className="p-2 border w-12 text-center">{index + 1}</td>
                  <td className="px-2 border">{mergedStatuses[statusKey]}</td>
                  <td className="px-2 py-1 border">
                    <select
                      className="block rounded py-1.5 px-2 w-full text-sm text-gray-900 bg-transparent border border-gray-200 appearance-none focus:outline-none focus:ring-0 focus:border-gray-200 peer"
                      value={current}
                      disabled={isLoading}
                      onChange={(e) => onChange(statusKey, e.target.value)}
                    >
                      <option value="">- Select -</option>
                      {Object.keys(actionTakenOptions).map((optKey) => (
                        <option key={optKey} value={optKey}>
                          {actionTakenOptions[optKey]}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
